import { ConfigProvider, Empty } from "antd";
import { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Route, Routes } from "react-router-dom";
import LoadingBar from "react-top-loading-bar";

import "./App.module.scss";
import "./styles/ant-overrides.scss";
import "./styles/globals.scss";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { InfoModal } from "./components/InfoModal/InfoModal";
import { ModalsContext } from "./context/modals.context";
import { Locales } from "./i18n";
import { UserRoles } from "./modules/auth/auth.constants";
import { AuthContext } from "./modules/auth/auth.context";
import { authService } from "./modules/auth/auth.service";
import { IRoutes } from "./modules/auth/auth.types";
import LoginPage from "./pages/LoginPage/LoginPage";
import storage from "./utils/storage";

const queryClient = new QueryClient();

const App = () => {
  const { loaderRef, modals } = useContext(ModalsContext);
  const { GET_ROUTES } = useContext(AuthContext);
  const { i18n, t } = useTranslation();

  const handleRoutes = (GET_ROUTES: (userRoles: UserRoles[]) => IRoutes[]) => {
    const roles = authService.getRole() || [];
    return GET_ROUTES(roles).map((route) => {
      if (route.nestedPaths) {
        return (
          <Route key={route.path} path={route.path} element={<route.element />}>
            {route.nestedPaths.map((nestedPath: any) => {
              return (
                <Route
                  key={nestedPath.path}
                  path={nestedPath.path}
                  element={<nestedPath.element />}
                />
              );
            })}
          </Route>
        );
      }
      return (
        <Route key={route.path} path={route.path} element={<route.element />} />
      );
    });
  };

  useEffect(() => {
    const languageSettings = storage.getItem("locale");
    if (languageSettings) {
      i18n.changeLanguage(languageSettings);
    } else {
      storage.setItem("locale", Locales.LATIN);
    }
  }, []);

  return (
    <ConfigProvider
      renderEmpty={() => (
        <Empty
          description={t("COMMON.NEMA_PODATAKA")}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      )}
    >
      <QueryClientProvider client={queryClient}>
        <div className="App">
          <LoadingBar color="#0364ca" ref={loaderRef} />
          <InfoModal
            message={modals.infoMessage}
            title={modals.infoTitle}
            onReload={modals.onReload}
          />
          <Routes>
            <Route path="/" element={<LoginPage />} />
            {handleRoutes(GET_ROUTES)}
          </Routes>
        </div>
      </QueryClientProvider>
    </ConfigProvider>
  );
};

export default App;
